import apiClient from './apiClient'

class ImageGenerationService {
  /**
   * Calculate aspect ratio from image file
   * Returns closest supported ratio: "1:1" | "2:3" | "3:2" | "3:4" | "4:3" | "9:16" | "16:9" | "21:9"
   */
  async getAspectRatio(imageFile) {
    return new Promise((resolve) => {
      const img = new Image()
      const url = URL.createObjectURL(imageFile)

      img.onload = () => {
        const width = img.width
        const height = img.height
        const ratio = width / height

        // Clean up
        URL.revokeObjectURL(url)

        // Map to closest supported aspect ratio
        const ratios = [
          { value: '1:1', ratio: 1 },
          { value: '2:3', ratio: 2 / 3 },
          { value: '3:2', ratio: 3 / 2 },
          { value: '3:4', ratio: 3 / 4 },
          { value: '4:3', ratio: 4 / 3 },
          { value: '9:16', ratio: 9 / 16 },
          { value: '16:9', ratio: 16 / 9 },
          { value: '21:9', ratio: 21 / 9 },
        ]

        // Find closest ratio
        let closest = ratios[0]
        let minDiff = Math.abs(ratio - closest.ratio)

        ratios.forEach((r) => {
          const diff = Math.abs(ratio - r.ratio)
          if (diff < minDiff) {
            minDiff = diff
            closest = r
          }
        })

        resolve(closest.value)
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        resolve('1:1') // Default fallback
      }

      img.src = url
    })
  }

  /**
   * Create image generation job
   * POST /api/v1/images/generate
   */
  async createJob(imageFile, prompt) {
    try {
      // Calculate aspect ratio from uploaded image
      const aspectRatio = await this.getAspectRatio(imageFile)

      // Create FormData for multipart/form-data
      const formData = new FormData()
      formData.append('initial_image', imageFile)
      formData.append('prompt', prompt)
      formData.append('aspect_ratio', aspectRatio)

      // Get JWT from localStorage
      const jwt = localStorage.getItem('stytch_session_jwt')

      // Make API request directly using request() method
      // Don't use post() because it calls JSON.stringify() on the FormData
      const response = await apiClient.request('/api/v1/images/generate', {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type - browser will automatically set multipart/form-data with boundary
          ...(jwt && { Authorization: `Bearer ${jwt}` }),
        },
      })

      return {
        success: true,
        job_id: response.job_id,
        status: response.status,
      }
    } catch (error) {
      console.error('Failed to create image generation job:', error)

      // Parse specific error types
      const errorMessage = error.message || 'Failed to create generation job'

      if (errorMessage.includes('401')) {
        return { success: false, error: 'SESSION_EXPIRED', message: errorMessage }
      }
      if (errorMessage.includes('403')) {
        return { success: false, error: 'SUBSCRIPTION_REQUIRED', message: errorMessage }
      }
      if (errorMessage.includes('429')) {
        return { success: false, error: 'CREDITS_EXHAUSTED', message: errorMessage }
      }

      return {
        success: false,
        error: 'GENERATION_FAILED',
        message: errorMessage,
      }
    }
  }

  /**
   * Poll job status until completed/failed
   * GET /api/v1/images/jobs/{id}
   * Max 30 attempts × 2s = 60s timeout
   */
  async pollJobStatus(jobId) {
    const maxAttempts = 30
    const pollInterval = 2000 // 2 seconds

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const job = await apiClient.get(`/api/v1/images/jobs/${jobId}`)

        // Check if job is complete
        if (job.status === 'completed' || job.status === 'failed') {
          return job
        }

        // Wait before next poll (skip wait on last attempt)
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval))
        }
      } catch (error) {
        console.error(`Failed to poll job status (attempt ${attempt + 1}/${maxAttempts}):`, error)

        // If it's a 404, the job doesn't exist - fail immediately
        if (error.message?.includes('404')) {
          throw new Error('Job not found')
        }

        // For other errors, continue polling (might be temporary network issue)
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval))
        }
      }
    }

    throw new Error('Job timeout: exceeded maximum wait time (60 seconds)')
  }

  /**
   * Main method: Generate images
   * Replaces geminiService.generateImages()
   * Returns array of image URLs or error
   */
  async generateImages(imageFile, prompt) {
    try {
      // Step 1: Create job
      const jobResult = await this.createJob(imageFile, prompt)

      if (!jobResult.success) {
        return jobResult // Return error from createJob
      }

      // Step 2: Poll for completion
      const job = await this.pollJobStatus(jobResult.job_id)

      // Step 3: Return results
      if (job.status === 'completed') {
        return {
          success: true,
          images: job.generated_image_urls, // Array of 4 URLs
          thoughtSignature: job.thought_signature,
        }
      } else {
        // Job failed
        return {
          success: false,
          error: 'GENERATION_FAILED',
          message: job.error_message || 'Image generation failed',
        }
      }
    } catch (error) {
      console.error('Image generation error:', error)

      return {
        success: false,
        error: 'GENERATION_FAILED',
        message: error.message || 'An unexpected error occurred during image generation',
      }
    }
  }
}

export default new ImageGenerationService()
